import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Cloud, Cpu, GitBranch, Activity, Search, Bug, Lock,
  ArrowRight, Sparkles, LifeBuoy, Layers, Rocket,
} from "lucide-react";
import "./CloudSolutions.css";
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const pillars = [
  {
    icon: Cpu,
    label: "Build",
    title: "Cloud Native Development",
    points: [
      "Custom application development, monitoring and scaling matching PaaS, IaaS or SaaS needs.",
      "Spread across mobile, IoT, Ecommerce or machine learning.",
      "Determine Optimal Data Strategies.",
    ],
  },
  {
    icon: Cloud,
    label: "Migrate",
    title: "Cloud Analysis and Migration",
    points: [
      "Establishing the right cloud architecture.",
      "Building transition roadmaps that ensure adoption.",
      "Azure, AWS, Google Cloud Support & Migration.",
      "Determining areas of your existing architecture that should be migrated or phased out.",
      "Providing clear ROI and TCO.",
      "O365 Migration & Support.",
    ],
  },
  {
    icon: GitBranch,
    label: "Automate",
    title: "DevOps Automation",
    points: [
      "Using DevOps as a methodology with cloud services allows us to follow a CI-CD model for pipeline automation when it comes to Application, Database or cloud services.",
    ],
  },
];

const threats = [
  { icon: Search, ribbon: "Assess", title: "Vulnerability Assessment", desc: "Tool Based and Manual Approach." },
  { icon: Bug, ribbon: "Test", title: "Penetration Testing", desc: "Tool Based and Manual Approach." },
  { icon: Lock, ribbon: "Harden", title: "Hardening", desc: "System hardening across infrastructure layers." },
];

const managedSec = [
  "24x7 Monitoring potential threats, compliance violations or vulnerability using a Security Platform and triage the alerts.",
  "Utilizing specialized threat hunting platform and expertise to discover security flaws in end points, user behavior, network and application.",
  "Cyber Investigating and issue resolution.",
];

const related = [
  { icon: LifeBuoy, title: "Product Support Services", to: "/services/product-support-services" },
  { icon: Layers, title: "AI Enabled GCC Services", to: "/services/gcc-services" },
  { icon: Rocket, title: "New Product development", to: "/services/new-product-development" },
];

const CloudSolutions = () => {
  return (
    <div className="cs-page">
      {/* HERO */}
      <section className="cs-hero">
        <div className="cs-container">
          <motion.div
            initial="hidden" animate="visible" variants={fadeUp}
            className="cs-hero-inner"
          >
            <span className="cs-eyebrow">
              <Sparkles size={14} /> Uncover Our Approach Towards
            </span>
            <h1 className="cs-hero-title">
              New <span className="cs-hero-accent">Cloud Solutions</span>
            </h1>
            <p className="cs-hero-subtitle">
              Empower your business with secure, scalable, and future-ready cloud
              solutions designed to accelerate innovation, improve performance, and
              drive digital transformation.
            </p>
            <div className="cs-hero-actions">
              <Link to="/contact" className="cs-cta1">
                Connect with us <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO + CAPABILITIES */}
      <section className="cs-section " id="capabilities">
        <div className="cs-container container">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="cs-section-head"
          >
            <span className="cs-tag">Cloud Solutions</span>
            <h2>Our cloud capabilities</h2>
            <p>
              With the movement towards a multi or hybrid cloud environment using public cloud
              platforms such as AWS, Azure or an Enterprise Level private cloud platform; there is a huge
              surge for cloud migration services. Our calibrated approach helps our customers to migrate from
              Legacy Ecosystem to Modern Cloud environment.
            </p>
          </motion.div>

          <div className="cs-pillar-grid ">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="cs-pillar-card"
              >
                <div className="cs-pillar-top">
                  <div className="cs-pillar-icon"><p.icon size={24} /></div>
                  <span className="cs-pillar-num">0{i + 1}</span>
                </div>
                <span className="cs-pillar-label">{p.label}</span>
                <h4>{p.title}</h4>
                <ul className="cs-pillar-list">
                  {p.points.map((pt) => (
                    <li key={pt}>{pt}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MANAGED SECURITY */}
      <section className="cs-section cs-section--alt">
        <div className="cs-container container">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="cs-section-head"
          >
            <span className="cs-tag cs-tag--blue">Managed Security</span>
            <h2>Threat Management</h2>
          </motion.div>

          <div className="cs-threat-grid">
            {threats.map((t, i) => (
              <motion.div
                key={t.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="cs-threat-card"
              >
                <span className="cs-threat-circle" />
                <div className="cs-threat-ribbon">{t.ribbon}</div>
                <div className="cs-threat-head">
                  <div className="cs-threat-orb"><t.icon size={26} /></div>
                  <h4>{t.title}</h4>
                </div>
                <div className="cs-threat-body">
                  <p>{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="cs-managed-block"
          >
            <span className="cs-corner cs-corner--tr" />
            <span className="cs-corner cs-corner--bl" />
            <div className="cs-managed-inner">
              <div className="cs-managed-head">
                <div className="cs-managed-icon"><Activity size={26} /></div>
                <div>
                  <span className="cs-managed-eyebrow">Always-on protection</span>
                  <h3>Managed Security Solutions</h3>
                </div>
              </div>
              <div className="cs-managed-list">
                {managedSec.map((m, i) => (
                  <motion.div
                    key={i} custom={i}
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                    className="cs-managed-item"
                  >
                    <span className="cs-managed-num">0{i + 1}</span>
                    <p>{m}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* RELATED */}
      <section className="cs-section">
        <div className="cs-container container">
          <motion.h2
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="cs-related-title"
          >
            Related Services
          </motion.h2>
          <div className="cs-related-grid">
            {related.map((r, i) => (
              <motion.div
                key={r.title} custom={i}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="cs-related-card"
              >
                <div className="cs-related-icon"><r.icon size={22} /></div>
                <h4>{r.title}</h4>
                <Link to={r.to} className="cs-related-link">
                  Learn more <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CloudSolutions;
